import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { appointmentsApi, appointmentQueryKeys } from "@/shared/api/appointments";
import { apiError } from "@/shared/api/client";
import { encountersApi } from "@/shared/api/encounters";
import { roomsApi, roomQueryKeys } from "@/shared/api/rooms";
import { servicesApi, servicesQueryKeys } from "@/shared/api/services";
import { formatVnd } from "@/shared/format";
import {
  useBookingDimensions,
  validateBookingDimensions,
  type BookingDimension,
} from "@/shared/hooks/useBookingDimensions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DepartmentSelect } from "@/shared/ui/DepartmentSelect";
import { OptionCombobox } from "@/shared/ui/OptionCombobox";

/** What the workspace can ask of the panel when the doctor signs (TD-7b, #18800). */
export interface FollowUpPanelHandle {
  /**
   * Books the tái khám if a future date is set and that date has not already been booked from this
   * panel; resolves either way, never throws.
   */
  createIfScheduled(): Promise<void>;
}


function isoToVn(iso: string): string {
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

/**
 * Ngày hẹn kế tiếp (tái khám) — the follow-up booking, extracted from the workspace.
 *
 * <p><b>Owns the booking dimensions, not the date.</b> Khoa/Phòng/Bác sĩ/Dịch vụ, their two
 * lookups, the policy validation and the success/warning lines all live here — they existed
 * nowhere else. The DATE stays with the workspace on purpose: picking it also rewrites the
 * "Hẹn tái khám: dd/mm/yyyy" line inside the SOAP plan, which is encounter state the workspace
 * owns. Pulling the date down here would have meant handing the panel a SOAP patcher, which is
 * how a panel ends up knowing about the clinical note (§9).
 *
 * <p><b>Why an imperative handle rather than more props.</b> Three sign paths need to book the
 * follow-up after the encounter is signed. The alternative was lifting all six state slots back
 * into the parent so it could build the request — which is the §5 trap: ten files that still pass
 * thirty props. One ref and one method is the smaller contract.
 *
 * <p>Failures are soft by design: a follow-up that cannot be booked shows a warning and never
 * blocks the signature.
 */
export const FollowUpPanel = forwardRef<
  FollowUpPanelHandle,
  {
    encounterId: string;
    /** ISO yyyy-MM-dd, owned by the workspace because it is mirrored into the SOAP plan. */
    date: string;
    onDateChange: (iso: string) => void;
    /** Today in ISO, so "future" means the same thing here as in the workspace. */
    today: string;
    /**
     * The treating doctor. Tái khám defaults to the same doctor (long-standing behaviour); Khoa /
     * Phòng / Dịch vụ stay blank so the backend inherits the khoa from this encounter.
     */
    defaultDoctorId: string | null;
    disabled: boolean;
  }
>(function FollowUpPanel(
  { encounterId, date, onDateChange, today, defaultDoctorId, disabled },
  ref
) {
  const [departmentId, setDepartmentId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [okMessage, setOkMessage] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Ngày đã đặt tái khám thành công trong lượt mở màn này. Nút "Tạo lịch tái khám" đặt ngay
  // (không cần ký), rồi bước ký lại gọi createIfScheduled() với đúng ngày đó — không nhớ thì mỗi
  // lượt khám sinh HAI lịch tái khám và bệnh nhân nhận hai tin xác nhận. Backend không có gì nối
  // lịch tái khám về lần khám nên không tự chặn được; chốt nằm ở đây.
  const [bookedDate, setBookedDate] = useState<string | null>(null);
  // Ref, không phải state: hai lời gọi sát nhau (bấm đúp, hoặc bấm nút rồi F9 ngay) đọc cùng một
  // giá trị state cũ, nên state không chặn được lần thứ hai.
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  // Seeds, never overrides: once the doctor has picked someone else, a re-render of the parent
  // must not silently put the treating doctor back.
  useEffect(() => {
    if (defaultDoctorId) setDoctorId((prev) => prev || defaultDoctorId);
  }, [defaultDoctorId]);

  const { dimensions, byKey } = useBookingDimensions();
  const visibleDimensions = useMemo(() => dimensions.filter((d) => d.visible), [dimensions]);

  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
    enabled: byKey.doctor?.visible ?? false,
  });
  const rooms = useQuery({
    queryKey: roomQueryKeys.list(true),
    queryFn: () => roomsApi.list(true),
    enabled: byKey.room?.visible ?? false,
  });
  // Shares the workspace's query key, so react-query serves this from cache rather than refetching.
  const services = useQuery({
    queryKey: servicesQueryKeys.all(),
    queryFn: () => servicesApi.listAll(),
    enabled: byKey.service?.visible ?? false,
  });

  // Phòng lọc theo khoa đã chọn (phòng không thuộc khoa nào vẫn hiện).
  const roomOptions = useMemo(() => {
    const all = rooms.data ?? [];
    return departmentId
      ? all.filter((r) => !r.departmentId || r.departmentId === departmentId)
      : all;
  }, [rooms.data, departmentId]);

  /*
   * Danh sách cho combobox — cùng nhãn "trống" mà <select> cũ dựng, giữ nguyên câu chữ người dùng
   * đã quen (#18593).
   */
  const roomChoices = useMemo(
    () => [
      { id: "", label: "— Chưa xếp phòng —" },
      ...roomOptions.map((r) => ({ id: r.id, label: r.name })),
    ],
    [roomOptions]
  );
  const doctorChoices = useMemo(
    () => [
      { id: "", label: "— Bác sĩ bất kỳ —" },
      ...(doctors.data ?? []).map((d) => ({ id: d.id, label: d.name })),
    ],
    [doctors.data]
  );
  const serviceChoices = useMemo(
    () => [
      { id: "", label: "— Không chọn —" },
      ...(services.data ?? []).map((s) => ({ id: s.id, label: s.name })),
    ],
    [services.data]
  );

  const isFuture = /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= today;

  const alreadyBooked = bookedDate !== null && bookedDate === date;

  async function create() {
    if (!isFuture || alreadyBooked || inFlight.current) return;
    setOkMessage(null);
    setWarning(null);
    // Chặn khi một chiều BẮT BUỘC (theo booking policy) đang hiển thị mà bỏ trống.
    const dimErrors = validateBookingDimensions(dimensions, {
      department: departmentId,
      room: roomId,
      doctor: doctorId,
      service: serviceId,
    });
    const firstErr = Object.values(dimErrors)[0];
    if (firstErr) {
      setWarning(firstErr);
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await encountersApi.createFollowUp(encounterId, date, {
        departmentId: byKey.department?.visible ? departmentId || null : null,
        roomId: byKey.room?.visible ? roomId || null : null,
        doctorId: byKey.doctor?.visible ? doctorId || null : null,
        serviceId: byKey.service?.visible ? serviceId || null : null,
      });
      const fee = res.examFeeAmount > 0 ? formatVnd(res.examFeeAmount) : "miễn/BHYT";
      setBookedDate(date);
      setOkMessage(
        `Đã tạo lịch tái khám ngày ${isoToVn(res.date)} · phiếu thu chưa thanh toán (${fee})`
      );
    } catch (e) {
      setWarning(`Không tạo được lịch tái khám: ${apiError(e).detail}`);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  useImperativeHandle(ref, () => ({ createIfScheduled: create }));

  function renderDimension(d: BookingDimension) {
    const req = d.required ? " *" : "";
    switch (d.key) {
      case "department":
        return (
          <div key="department" className="space-y-1">
            <Label className="text-xs">
              {d.label}
              {req}
            </Label>
            <DepartmentSelect
              value={departmentId || null}
              onChange={(v) => {
                setDepartmentId(v ?? "");
                setRoomId(""); // đổi khoa → bỏ phòng đã chọn
              }}
              disabled={disabled}
            />
          </div>
        );
      case "room":
        return (
          <div key="room" className="space-y-1">
            <Label className="text-xs">
              {d.label}
              {req}
            </Label>
            <OptionCombobox
              options={roomChoices}
              value={roomId}
              onChange={setRoomId}
              disabled={disabled}
            />
          </div>
        );
      case "doctor":
        return (
          <div key="doctor" className="space-y-1">
            <Label className="text-xs">
              {d.label}
              {req}
            </Label>
            <OptionCombobox
              options={doctorChoices}
              value={doctorId}
              onChange={setDoctorId}
              disabled={disabled}
            />
          </div>
        );
      case "service":
        return (
          <div key="service" className="space-y-1">
            <Label className="text-xs">
              {d.label}
              {req}
            </Label>
            <OptionCombobox
              options={serviceChoices}
              value={serviceId}
              onChange={setServiceId}
              disabled={disabled}
            />
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div className="mt-3 space-y-1.5">
      <Label htmlFor="exam-fu">Ngày hẹn kế tiếp (tái khám)</Label>
      {visibleDimensions.length > 0 && (
        // Sidebar is a fixed 320px column, so keep the Khoa/Phòng/… fields stacked (single
        // column) — sm:grid-cols-2 keyed off the viewport, not the container, and squeezed two
        // dropdowns into ~150px each so their values got cut off (#18593).
        //
        // minmax(0,1fr), không phải 1fr: một grid item mặc định có `min-width: auto`, nghĩa là nó
        // KHÔNG co nhỏ hơn nội dung của mình. Cột phình ra theo tên khoa dài, `w-full` của
        // DepartmentSelect bằng luôn chiều rộng đã phình, và `truncate` bên trong không bao giờ
        // kích hoạt — field tràn ra ngoài sidebar (#18593, lần mở lại thứ hai). Bản thân
        // DepartmentSelect đã truncate đúng; thứ thiếu nằm một tầng trên.
        <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
          {visibleDimensions.map(renderDimension)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id="exam-fu"
          type="date"
          className="w-auto"
          disabled={disabled}
          value={date}
          onChange={(ev) => onDateChange(ev.target.value)}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!isFuture || alreadyBooked || busy}
          onClick={create}
          title="Tạo lịch tái khám (không cần ký)"
        >
          <Plus className="mr-1 h-4 w-4" /> Tạo lịch tái khám
        </Button>
      </div>
      {okMessage && <p className="mt-1.5 text-[13px] text-emerald-600">{okMessage}</p>}
      {warning && <p className="mt-1.5 text-[13px] text-amber-600">{warning}</p>}
    </div>
  );
});
